import { z } from 'zod';

/**
 * Vocabulary for the member-record access audit: on what basis a staff caller
 * reached a member, and how they got to the member's page.
 *
 * The basis is server-computed. The entry point is client-supplied context —
 * informational only, never read by an authorization decision — so it arrives
 * in request headers and is parsed here into a closed vocabulary before it can
 * reach a stored row. The arrays are the source for the parser and for the
 * Drizzle schema's CHECK constraints, which a contract test compares with them.
 * The migration's CHECKs are hand-written SQL literals that nothing compares, so
 * a change here needs a migration that changes them in step.
 */

/**
 * `unresolved` is the one value the assignment predicate cannot produce: the
 * member resolved, but the caller has no internal identity to ask about, so no
 * basis can be decided.
 */
export const MEMBER_ACCESS_BASES = [
  'assigned',
  'recorded_justification',
  'unassigned',
  'unresolved',
] as const;
export type MemberAccessBasis = (typeof MEMBER_ACCESS_BASES)[number];

/** The values a client may send. */
export const MEMBER_ACCESS_ENTRY_POINTS = [
  'direct_link',
  'member_lookup',
  'escalation_link',
  'escalation_queue',
  'notification',
  'member_list',
  'sidebar',
  'appointments',
  'outreach_queue',
  'other',
] as const;
export type MemberAccessEntryPoint = (typeof MEMBER_ACCESS_ENTRY_POINTS)[number];

/**
 * What a row may hold: the client values plus `unspecified`, which only the
 * server writes — a request that carried no entry header at all. Kept apart
 * from `other` so "the client said something we do not recognise" and "the
 * client said nothing" stay distinguishable in the trail.
 */
export const MEMBER_ACCESS_STORED_ENTRY_POINTS = [
  ...MEMBER_ACCESS_ENTRY_POINTS,
  'unspecified',
] as const;
export type MemberAccessStoredEntryPoint = (typeof MEMBER_ACCESS_STORED_ENTRY_POINTS)[number];

/** The only entry points an escalation id may accompany. */
export const MEMBER_ACCESS_ESCALATION_ENTRY_POINTS = [
  'escalation_link',
  'escalation_queue',
] as const satisfies readonly MemberAccessEntryPoint[];

/** Lower-case, as Hono and the Fetch API normalise header names. */
export const MEMBER_ACCESS_ENTRY_POINT_HEADER = 'x-galapagos-entry-point';
export const MEMBER_ACCESS_ESCALATION_ID_HEADER = 'x-galapagos-escalation-id';

/** Longer than any vocabulary value or uuid; anything past it is not one of ours. */
const MAX_HEADER_LENGTH = 64;

const entryPointSchema = z.enum(MEMBER_ACCESS_ENTRY_POINTS);
const escalationIdSchema = z.uuid();
const escalationEntryPointSchema = z.enum(MEMBER_ACCESS_ESCALATION_ENTRY_POINTS);

export interface MemberAccessEntry {
  readonly entryPoint: MemberAccessStoredEntryPoint;
  readonly escalationId: string | null;
}

function bounded(raw: string | undefined): string | undefined {
  if (raw === undefined) return undefined;
  const trimmed = raw.trim();
  return trimmed.length > MAX_HEADER_LENGTH ? undefined : trimmed;
}

/**
 * Parse the two entry headers. Never throws and never rejects a request:
 * garbage degrades to `other` / no escalation, because the value is context for
 * the trail, not input the request depends on.
 *
 * An escalation id survives only beside an escalation entry point. A lookup
 * that also claims an escalation is not an escalation visit, and storing the id
 * there would attach an escalation to an access that never came through one.
 */
export function parseMemberAccessEntry(
  readHeader: (name: string) => string | undefined,
): MemberAccessEntry {
  const rawEntry = readHeader(MEMBER_ACCESS_ENTRY_POINT_HEADER);
  if (rawEntry === undefined) return { entryPoint: 'unspecified', escalationId: null };

  const parsedEntry = entryPointSchema.safeParse(bounded(rawEntry));
  const entryPoint: MemberAccessEntryPoint = parsedEntry.success ? parsedEntry.data : 'other';

  const parsedId = escalationIdSchema.safeParse(
    bounded(readHeader(MEMBER_ACCESS_ESCALATION_ID_HEADER)),
  );
  const escalationId =
    parsedId.success && escalationEntryPointSchema.safeParse(entryPoint).success
      ? parsedId.data.toLowerCase()
      : null;

  return { entryPoint, escalationId };
}
